import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Activity, Zap, Droplet, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

export default function UtilityReadings() {
  const { user } = useContext(AuthContext); // eslint-disable-line no-unused-vars
  const [readings, setReadings] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newReading, setNewReading] = useState({
    utility_type: 'electricity', building_id: '', meter_reference: '', reading_value: 0, unit: 'kWh',
    reading_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchBuildings(); }, []);
  useEffect(() => { if (selectedBuilding) fetchReadings(); }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const r = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(r.data);
      if (r.data.length > 0) setSelectedBuilding(r.data[0].id);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const fetchReadings = async () => {
    try {
      const r = await axios.get(`${API}/utility-readings?building_id=${selectedBuilding}`, { withCredentials: true });
      setReadings(r.data);
    } catch { toast.error('Failed'); }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/utility-readings`, {
        ...newReading,
        building_id: newReading.building_id || selectedBuilding,
        reading_value: parseFloat(newReading.reading_value),
        reading_date: new Date(newReading.reading_date).toISOString()
      }, { withCredentials: true });
      toast.success('Reading added');
      setShowNew(false);
      fetchReadings();
    } catch { toast.error('Failed'); }
  };

  const chartData = (type) => readings
    .filter(r => r.utility_type === type)
    .sort((a, b) => new Date(a.reading_date) - new Date(b.reading_date))
    .slice(-12)
    .map(r => ({ date: new Date(r.reading_date).toLocaleDateString(), value: r.reading_value }));

  return (
    <Layout>
      <div data-testid="utilities-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Utility Readings</h1>
            <p className="text-sm text-muted-foreground">Electricity, water and gas consumption</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
              <SelectTrigger data-testid="utility-building-select" className="w-48"><SelectValue placeholder="Building" /></SelectTrigger>
              <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-reading-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Reading</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Log Utility Reading</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Building *</Label>
                    <Select value={newReading.building_id || selectedBuilding} onValueChange={(v) => setNewReading({ ...newReading, building_id: v })}>
                      <SelectTrigger data-testid="reading-building-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Meter Reference *</Label>
                    <Input data-testid="meter-ref-input" value={newReading.meter_reference} onChange={(e) => setNewReading({ ...newReading, meter_reference: e.target.value })} placeholder="e.g. DEWA-3344-A" />
                  </div>
                  <div><Label>Type</Label>
                    <Select value={newReading.utility_type} onValueChange={(v) => {
                      const unit = v === 'electricity' ? 'kWh' : v === 'water' ? 'm³' : 'm³';
                      setNewReading({ ...newReading, utility_type: v, unit });
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="electricity">Electricity (kWh)</SelectItem>
                        <SelectItem value="water">Water (m³)</SelectItem>
                        <SelectItem value="gas">Gas (m³)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Reading Value</Label><Input type="number" data-testid="reading-value-input" value={newReading.reading_value} onChange={(e) => setNewReading({ ...newReading, reading_value: e.target.value })} /></div>
                    <div><Label>Date</Label><Input type="date" value={newReading.reading_date} onChange={(e) => setNewReading({ ...newReading, reading_date: e.target.value })} /></div>
                  </div>
                  <Button data-testid="submit-reading-btn" onClick={handleCreate} disabled={!newReading.meter_reference} className="w-full bg-teal-600 hover:bg-teal-700">Save Reading</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { type: 'electricity', label: 'Electricity', icon: Zap, color: '#eab308' },
              { type: 'water', label: 'Water', icon: Droplet, color: '#0ea5e9' },
              { type: 'gas', label: 'Gas', icon: Flame, color: '#f97316' }
            ].map((u) => {
              const data = chartData(u.type);
              const latest = data[data.length - 1]?.value || 0;
              const Icon = u.icon;
              return (
                <Card key={u.type} className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5" style={{ color: u.color }} />
                    <h3 className="font-semibold text-foreground">{u.label}</h3>
                  </div>
                  <div className="text-2xl font-bold text-foreground mb-3">{latest.toLocaleString()}<span className="text-sm font-normal text-slate-500"> {u.type === 'electricity' ? 'kWh' : 'm³'}</span></div>
                  {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart data={data}>
                        <Line type="monotone" dataKey="value" stroke={u.color} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-muted-foreground">No data</p>}
                </Card>
              );
            })}
          </div>
        )}

        {readings.length > 0 && (
          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-4">Recent Readings</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Building</th>
                    <th className="py-2 pr-3">Meter Ref.</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3 text-right">Reading</th>
                  </tr>
                </thead>
                <tbody>
                  {readings.slice().sort((a, b) => new Date(b.reading_date) - new Date(a.reading_date)).slice(0, 12).map(r => {
                    const b = buildings.find(x => x.id === r.building_id);
                    return (
                      <tr key={r.id} data-testid={`reading-row-${r.id}`} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 pr-3 text-slate-700">{new Date(r.reading_date).toLocaleDateString()}</td>
                        <td className="py-2 pr-3 font-medium text-slate-900">{b?.name || '—'}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-slate-700">{r.meter_reference || '—'}</td>
                        <td className="py-2 pr-3 capitalize text-slate-700">{r.utility_type}</td>
                        <td className="py-2 pr-3 text-right font-semibold">{r.reading_value?.toLocaleString()} <span className="text-xs text-muted-foreground">{r.unit}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {readings.length > 0 && (
          <Card className="p-5">
            <h3 className="font-semibold text-foreground mb-4">Consumption Trend</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData('electricity')}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} name="Reading" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {buildings.length === 0 && (
          <Card className="p-10 text-center">
            <Activity className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground">Create a building first to log utility readings</p>
          </Card>
        )}
      </div>
    </Layout>
  );
}
